import React, { useState, useRef, useEffect } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { convertToInches, formatValue } from '@/utils/utils';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Trash2, DoorClosed, Zap, Droplets, Flame, Grid2X2, SquareIcon, ChevronLeft, ChevronRight } from 'lucide-react';


const MAX_CONTAINER_SIZE = 1000;
const UTILITY_TOUCH_AREA = 20;
const MIN_WALL_HEIGHT = 200;

const WallElementEditor = ({ 
  wallWidth, 
  wallHeight, 
  unit, 
  elements, 
  onElementsChange,
  containerHeight,
  containerWidth,
  wallName
}) => {
  const [selectedElement, setSelectedElement] = useState(null);
  const [draggedElement, setDraggedElement] = useState(null);
  const wallRef = useRef(null);
  const toolbarRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: MAX_CONTAINER_SIZE, height: MAX_CONTAINER_SIZE });
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const aspectRatio = wallWidth / wallHeight;
  const SCROLL_AMOUNT = 200;

  const ELEMENT_TYPES = {
    window: { 
      label: 'Window', 
      color: 'lightblue', 
      defaultSize: { width: 47, height: 36 }, 
      fixedHeight: 50,
      resizable: true,
      icon: Grid2X2,
      description: 'Add a window',
      style: {
        border: 'border-gray-700',
        hover: 'hover:bg-gray-50',
        text: 'text-gray-600'
      }
    },
    door: { 
      label: 'Door', 
      color: 'brown', 
      defaultSize: { width: 32, height: 84 }, 
      resizable: true, 
      floorOnly: true,
      icon: DoorClosed,
      description: 'Add a door',
      style: {
        border: 'border-gray-600',
        hover: 'hover:bg-gray-50',
        text: 'text-gray-700'
      }
    },
    opening: {
      label: 'Opening', 
      color: 'white', 
      defaultSize: { width: wallWidth/2, height: wallHeight }, 
      resizable: true, 
      floorOnly: true,
      icon: SquareIcon,
      description: 'Add an opening',
      style: {
        border: 'border-gray-500',
        hover: 'hover:bg-gray-50',
        text: 'text-gray-600'
      }
    },
    electricity: { 
      label: 'Electricity', 
      color: 'yellow', 
      defaultSize: { width: 4, height: 4 }, 
      fixedHeight: 20,
      isUtility: true,
      icon: Zap,
      description: 'Add an electrical outlet',
      style: {
        border: 'border-yellow-300',
        hover: 'hover:bg-yellow-50',
        text: 'text-yellow-600'
      }
    },
    water: { 
      label: 'Water', 
      color: 'blue', 
      defaultSize: { width: 4, height: 4 }, 
      fixedHeight: 20,
      isUtility: true,
      icon: Droplets,
      description: 'Add a water connection',
      style: {
        border: 'border-blue-300',
        hover: 'hover:bg-blue-50',
        text: 'text-blue-600'
      }
    },
    gas: { 
      label: 'Gas', 
      color: 'red', 
      defaultSize: { width: 4, height: 4 }, 
      fixedHeight: 20,
      isUtility: true,
      icon: Flame,
      description: 'Add a gas connection',
      style: {
        border: 'border-red-300',
        hover: 'hover:bg-red-50',
        text: 'text-red-600'
      }
    }
  };

  const isValidElement = (index) => {
    return index !== null && 
           elements && 
           elements[index] && 
           typeof elements[index].width !== 'undefined' && 
           typeof elements[index].height !== 'undefined';
  };

  useEffect(() => {
    setSelectedElement(null);
  }, [wallName]);

  
  useEffect(() => {
    const updateContainerSize = () => {
      const availableHeight = containerHeight - 150;
      const availableWidth = containerWidth - 150;
      
      // Calculate the scaling factor for both dimensions
      const scaleWidth = availableWidth / wallWidth;
      const scaleHeight = availableHeight / wallHeight;
      
      // Use the smaller scale to ensure both dimensions fit while maintaining aspect ratio
      const scale = Math.min(scaleWidth, scaleHeight);
      
      // Apply the scale to both dimensions
      const width = wallWidth * scale;
      const height = wallHeight * scale;
      
      setContainerSize({ width, height });
    };
  
    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);
    return () => window.removeEventListener('resize', updateContainerSize);
  }, [aspectRatio, containerHeight, containerWidth, wallWidth, wallHeight, selectedElement]);

  useEffect(() => {
    const checkScrollability = () => {
      if (toolbarRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = toolbarRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
      }
    };
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, []);


  // Scale conversion utilities
  const scaleX = containerSize.width / wallWidth;
  const scaleY = containerSize.height / wallHeight;
  const convertToPixelsX = (value) => value * scaleX;
  const convertToPixelsY = (value) => value * scaleY;
  const convertFromPixelsX = (pixels) => Number((pixels / scaleX).toFixed(2));
  const convertFromPixelsY = (pixels) => Number((pixels / scaleY).toFixed(2));

  // Existing element manipulation functions
  const addElement = (elementType) => {
    const defaultSize = ELEMENT_TYPES[elementType].defaultSize;    
    let x = Math.random() * (wallWidth - defaultSize.width);
    let y = Math.random() * (wallHeight - defaultSize.height);
    
    if (ELEMENT_TYPES[elementType].floorOnly) {
      y = 0;
    }
    if (ELEMENT_TYPES[elementType].fixedHeight) {
      y = ELEMENT_TYPES[elementType].fixedHeight;
    }
    
    const newElement = {
      type: elementType,
      x,
      y,
      width: defaultSize.width,
      height: defaultSize.height
    };    
    onElementsChange([...elements, newElement]);
  };

  const removeElement = (index) => {
    onElementsChange(elements.filter((_, i) => i !== index));
    setSelectedElement(null);
  };

  const handleElementClick = (e, index) => {
    e.stopPropagation();
    setSelectedElement(index === selectedElement ? null : index);
  };

  const handleElementUpdate = (index, field, value) => {
    const newValue = value === '' ? 0 : parseFloat(value);
    if (!isNaN(newValue) && newValue >= 0) {
      const updatedElements = elements.map((element, i) => {
        if (i === index) {
          const updatedElement = { ...element, [field]: newValue };
          if (field === 'width') {
            updatedElement.x = Math.min(updatedElement.x, wallWidth - newValue);
          } else if (field === 'height') {
            updatedElement.y = Math.min(updatedElement.y, wallHeight - newValue);
          } else if (field === 'x') {
            updatedElement.x = Math.min(newValue, wallWidth - element.width);
          } else if (field === 'y') {
            updatedElement.y = Math.min(newValue, wallHeight - element.height);
          }
          return updatedElement;
        }
        return element;
      });
      onElementsChange(updatedElements);
    }
  };

  const handleDragStart = (e, index) => {
    const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
    const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;
    setDraggedElement({ index, startX: clientX, startY: clientY });
  };

  const handleDrag = (e) => {
    if (draggedElement && wallRef.current) {
      const clientX = e.type.startsWith('touch') ? e.touches[0].clientX : e.clientX;
      const clientY = e.type.startsWith('touch') ? e.touches[0].clientY : e.clientY;
      const rect = wallRef.current.getBoundingClientRect();
      const deltaX = convertFromPixelsX(clientX - draggedElement.startX);
      const deltaY = convertFromPixelsY(draggedElement.startY - clientY);
      
      const updatedElements = elements.map((element, index) => {
        if (index === draggedElement.index) {
          let newX = Math.max(0, Math.min(element.x + deltaX, wallWidth - element.width));
          let newY = Math.max(0, Math.min(element.y + deltaY, wallHeight - element.height));
          
          if (ELEMENT_TYPES[element.type].floorOnly) {
            newY = 0;
          }
          
          return { ...element, x: newX, y: newY };
        }
        return element;
      });
      
      onElementsChange(updatedElements);
      setDraggedElement({ ...draggedElement, startX: clientX, startY: clientY });
    }
  };

  const handleDragEnd = () => {
    setDraggedElement(null);
  };

  useEffect(() => {
    if (draggedElement) {
      const handleMouseMove = (e) => handleDrag(e);
      const handleTouchMove = (e) => {
        e.preventDefault(); // Prevent scrolling while dragging
        handleDrag(e);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchend', handleDragEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [draggedElement]);


  const renderArrows = (element, index) => {
    if (selectedElement !== index) return null;

    const xArrowLength = convertToPixelsX(element.x);
    const yArrowLength = convertToPixelsY(element.y);

    return (
      <>
        {/* X-axis arrow */}
        <div
          style={{
            position: 'absolute',
            left: '0',
            bottom: `${convertToPixelsY(element.y)}px`,
            width: `${xArrowLength}px`,
            height: '2px',
            backgroundColor: 'green',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: '0',
            bottom: `${convertToPixelsY(element.y) - 4}px`,
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: '8px solid green',
            transform: 'rotate(-90deg)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${xArrowLength / 2}px`,
            bottom: `${convertToPixelsY(element.y) + 5}px`,
            color: 'green',
            fontSize: '12px',
            whiteSpace: 'nowrap',
          }}
        >
          {formatValue(element.x, unit)} {unit}
        </div>

        {/* Y-axis arrow */}
        <div
          style={{
            position: 'absolute',
            left: `${convertToPixelsX(element.x)}px`,
            bottom: '0',
            width: '2px',
            height: `${yArrowLength}px`,
            backgroundColor: 'blue',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${convertToPixelsX(element.x) - 4}px`,
            bottom: '0',
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: '8px solid blue',
            transform: 'rotate(180deg)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            left: `${convertToPixelsX(element.x)}px`,
            bottom: `${yArrowLength / 2}px`,
            color: 'blue',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            transform: 'rotate(-90deg)',
            transformOrigin: 'left bottom',
          }}
        >
          {formatValue(element.y, unit)} {unit}
        </div>
      </>
    );
  };

  const handleScroll = () => {
    if (toolbarRef.current) {
      const { scrollWidth, clientWidth, scrollLeft } = toolbarRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  // Handle touch scrolling
  const handleTouchStart = (e) => {
    setTouchStart({
      x: e.touches[0].clientX,
      scrollLeft: toolbarRef.current.scrollLeft,
    });
  };

  const handleTouchMove = (e) => {
    if (!touchStart) return;
    
    const deltaX = touchStart.x - e.touches[0].clientX;
    if (toolbarRef.current) {
      toolbarRef.current.scrollLeft = touchStart.scrollLeft + deltaX;
    }
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
  };

  // Scroll toolbar
  const scrollToolbar = (direction) => {
    if (toolbarRef.current) {
      const newScrollLeft = toolbarRef.current.scrollLeft + (direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT);
      toolbarRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Inside WallElementEditor component, add this new function before the return statement:

const renderWallDimensions = () => {
  const OFFSET = 10; // Offset from the wall for the dimension arrows
  
  return (
    <>
      {/* Width dimension arrow */}
      <div style={{
        position: 'absolute',
        left: '-10px',
        top: `${containerSize.height + OFFSET}px`,
        width: `${containerSize.width + 20}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Left arrowhead */}
        <div style={{
          position: 'absolute',
          left: '0',
          width: '0',
          height: '0',
          borderTop: '6px solid transparent',
          borderBottom: '6px solid transparent',
          borderRight: '10px solid black'
        }} />
        
        {/* Line */}
        <div style={{
          width: '100%',
          height: '2px',
          backgroundColor: 'black'
        }} />
        
        {/* Right arrowhead */}
        <div style={{
          position: 'absolute',
          right: '0',
          width: '0',
          height: '0',
          borderTop: '6px solid transparent',
          borderBottom: '6px solid transparent',
          borderLeft: '10px solid black'
        }} />
        
        {/* Dimension text */}
        <div style={{
          position: 'absolute',
          width: '100%',
          textAlign: 'center',
          top: '10px',
          color: 'black',
          fontSize: '14px'
        }}>
          {formatValue(wallWidth, unit)} {unit}
        </div>
      </div>

      {/* Height dimension arrow */}
      <div style={{
        position: 'absolute',
        top: '-10px',
        left: `${-OFFSET}px`,
        height: `${containerSize.height + 20}px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Top arrowhead */}
        <div style={{
          position: 'absolute',
          top: '0',
          width: '0',
          height: '0',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: '10px solid black'
        }} />
        
        {/* Line */}
        <div style={{
          width: '2px',
          height: '100%',
          backgroundColor: 'black'
        }} />
        
        {/* Bottom arrowhead */}
        <div style={{
          position: 'absolute',
          bottom: '0',
          width: '0',
          height: '0',
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '10px solid black'
        }} />
        
        {/* Dimension text */}
        <div style={{
          position: 'absolute',
          height: '100%',
          left: '-30px',
          display: 'flex',
          alignItems: 'center',
          color: 'black',
          fontSize: '14px',
          transform: 'rotate(-90deg)',
          transformOrigin: 'center center',
          whiteSpace: 'nowrap'
        }}>
          {formatValue(wallHeight, unit)} {unit}
        </div>
      </div>
    </>
  );
};

  return (
    <div className="flex flex-col h-full" style={{ maxHeight: containerHeight }}>
      <div className="flex-none border-b relative">
        {showScrollButtons && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 ${!canScrollLeft && 'opacity-50 cursor-not-allowed'}`}
              onClick={() => scrollToolbar('left')}
              disabled={!canScrollLeft}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 ${!canScrollRight && 'opacity-50 cursor-not-allowed'}`}
              onClick={() => scrollToolbar('right')}
              disabled={!canScrollRight}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
        <div 
          ref={toolbarRef}
          className="overflow-x-auto scrollbar-hide px-8"
          onScroll={handleScroll}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            scrollbarWidth: 'none',  /* Firefox */
            msOverflowStyle: 'none',  /* IE and Edge */
            '&::WebkitScrollbar': {
              display: 'none'  /* Chrome, Safari and Opera */
            }
          }}
        >
          <div className="flex gap-2 p-4 min-w-max">
            <TooltipProvider>
              {Object.entries(ELEMENT_TYPES).map(([key, { label, icon: Icon, description, style }]) => (
                

                <Tooltip key={key}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      size="icon"
                      onClick={() => addElement(key)}
                      className={`w-14 h-14 shrink-0 rounded-lg bg-white border-2 ${style.border} ${style.hover} ${style.text} shadow-lg hover:shadow-xl transition-all duration-200 ease-in-out transform hover:scale-105 flex flex-col items-center justify-center gap-1 p-1`}

                    >
                      <Icon className="w-6 h-6" />
                      <span className="text-[10px] font-medium leading-none">
                        {label}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{description}</TooltipContent>
                </Tooltip>
              ))}
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Wall Editor Area - flexible height */}
      <div className="flex-grow overflow-auto p-4">
        <div className="flex items-center justify-center">
          <div
            ref={wallRef}
            style={{
              width: `${containerSize.width}px`,
              height: `${containerSize.height}px`,
              backgroundColor: 'lightgray',
              position: 'relative',
              border: '2px solid black',
              touchAction: 'none',
              marginLeft: '50px',
              marginBottom: '50px'
            }}
          >
            {elements.map((element, index) => {
              const isUtility = ELEMENT_TYPES[element.type].isUtility;
              const visualWidth = convertToPixelsX(element.width);
              const visualHeight = convertToPixelsY(element.height);
              const touchWidth = isUtility ? UTILITY_TOUCH_AREA : visualWidth;
              const touchHeight = isUtility ? UTILITY_TOUCH_AREA : visualHeight;
              
              return (
                <React.Fragment key={index}>
                  <div
                    style={{
                      position: 'absolute',
                      left: `${convertToPixelsX(element.x) - (isUtility ? (touchWidth - visualWidth) / 2 : 0)}px`,
                      bottom: `${convertToPixelsY(element.y) - (isUtility ? (touchHeight - visualHeight) / 2 : 0)}px`,
                      width: `${touchWidth}px`,
                      height: `${touchHeight}px`,
                      cursor: 'move',
                      touchAction: 'none',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      // Make touch area visible only when selected
                      backgroundColor: isUtility ? 'transparent' : ELEMENT_TYPES[element.type].color,
                      border: selectedElement === index ? '2px solid blue' : 'none',
                      boxShadow: selectedElement === index ? '0 0 10px rgba(0, 0, 255, 0.5)' : 'none',
                    }}
                    onClick={(e) => handleElementClick(e, index)}
                    onMouseDown={(e) => handleDragStart(e, index)}
                    onTouchStart={(e) => handleDragStart(e, index)}
                  >
                    {isUtility ? (
                      <div
                        style={{
                          width: `${visualWidth}px`,
                          height: `${visualHeight}px`,
                          backgroundColor: ELEMENT_TYPES[element.type].color,
                          border: '1px solid black',
                        }}
                      />
                    ) : (
                      ELEMENT_TYPES[element.type].label
                    )}
                  </div>
                  {renderArrows(element, index)}
                </React.Fragment>
              );
            })}
            {renderWallDimensions()}
          </div>
        </div>
      </div>

      {/* Selected Element Editor - fixed height when present */}
      {selectedElement !== null && isValidElement(selectedElement) && (
        <div className="flex-none border-t p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Label>Width:</Label>
              <Input
                type="number"
                value={formatValue(elements[selectedElement].width, unit)}
                onChange={(e) => handleElementUpdate(selectedElement, 'width', convertToInches(e.target.value, unit))}
                className="w-20"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>Height:</Label>
              <Input
                type="number"
                value={formatValue(elements[selectedElement].height, unit)}
                onChange={(e) => handleElementUpdate(selectedElement, 'height', convertToInches(e.target.value, unit))}
                className="w-20"
              />
            </div>
            <Button 
              onClick={() => removeElement(selectedElement)}
              variant="destructive"
              size="icon"
              className="ml-auto"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );


};

export default WallElementEditor;